import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { glideDurationMs, runGlide } from '../../ui/navGlideMotion';

/**
 * Drives ONE selection card (desktop rail, tablet dock, phone bar) imperatively
 * through the shared glide engine — transforms written straight to the node, no
 * React render per frame.
 *
 * - `getTarget(host)` -> `{ id, pos, size? }` or null (hide). `id` identifies the
 *   active item; an id change glides, anything else (resize, badge, label swap)
 *   snaps in lockstep. The target is re-read every frame, so a moving target
 *   (the phone chip morphing) is tracked exactly.
 * - First placement, a new indicator node (remount) and a `resetKey` change place
 *   with NO glide.
 * - `axis: 'x'` -> `translate(pos, -50%)` + width (card is `top: 50%`);
 *   `axis: 'y'` -> `translateY(pos)` + height.
 */
export default function useGlideIndicator({
  hostRef, indicatorRef, enabled = true, axis, getTarget, getBounds, observe,
  resetKey = '', reducedMotion = false, minMs = 180, stretch = 0, deps = [],
}) {
  const st = useRef({ el: null, hostNode: null, elNode: null, id: null, reset: null, last: null, cancel: null });
  const [nodeTick, setNodeTick] = useState(0);

  useLayoutEffect(() => {
    const s = st.current;
    const host = hostRef.current;
    const el = indicatorRef.current;
    s.hostNode = host;
    s.elNode = el;
    const stopGlide = () => { if (s.cancel) s.cancel(); s.cancel = null; };
    const hide = () => {
      stopGlide(); s.id = null; s.last = null;
      if (el) el.style.display = 'none';
    };
    if (!enabled || !host || !el) { hide(); return undefined; }
    const initial = getTarget(host);
    if (!initial) { hide(); return undefined; }

    const apply = (f) => {
      s.last = f;
      const sx = f.stretch && f.stretch !== 1 ? ` scaleX(${f.stretch})` : '';
      el.style.transform = axis === 'x' ? `translate(${f.pos}px, -50%)${sx}` : `translateY(${f.pos}px)`;
      if (f.size != null) el.style.setProperty(axis === 'x' ? 'width' : 'height', `${f.size}px`);
    };
    const live = () => getTarget(host) || initial;
    const fresh = s.el !== el || s.id == null || s.reset !== resetKey;
    s.el = el;
    s.reset = resetKey;
    el.style.display = '';
    if (fresh) {
      stopGlide();
      apply(initial);
      s.id = initial.id;
    } else if (initial.id !== s.id) {
      stopGlide();
      s.id = initial.id;
      const from = s.last || initial;
      // Reduced motion (or no rAF) finishes synchronously and calls onDone BEFORE
      // runGlide returns — only keep the cancel if the glide is still running, or
      // the resize/re-measure snaps below would think a glide is forever in flight.
      let finished = false;
      const done = () => { finished = true; s.cancel = null; };
      const cancel = runGlide({
        from: { pos: from.pos, size: from.size },
        getTarget: live,
        durationMs: Math.max(minMs, glideDurationMs(initial.pos - from.pos)),
        reducedMotion,
        stretch,
        bounds: getBounds ? () => getBounds(live(), host) : undefined,
        onFrame: apply,
        onDone: done,
      });
      if (!finished) s.cancel = cancel;
    } else if (!s.cancel) {
      apply(initial);
    }

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => { if (!s.cancel) { const t = getTarget(host); if (t) apply(t); } });
    (observe ? observe(host) : [host]).forEach((n) => observer.observe(n));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, axis, resetKey, reducedMotion, nodeTick, ...deps]);

  // The track / card ELEMENT was replaced (breakpoint wrapper swap, remount):
  // the effect above bound the old nodes, so re-run it against the new ones.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs every commit on purpose; the guard stops the loop
  useLayoutEffect(() => {
    const s = st.current;
    if (hostRef.current !== s.hostNode || indicatorRef.current !== s.elNode) setNodeTick((n) => n + 1);
  });

  useEffect(() => () => { if (st.current.cancel) st.current.cancel(); }, []);
}
